import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  deleteSurveyBatch,
  deleteSurveySubmission,
  getEmployeesLookup,
  getSurveyBatchById,
  getSurveyBatches,
  getSurveyFormLookupById,
  getSurveyFormsLookup,
  getSurveySubmissionById,
  getSurveySubmissions,
  sendSurvey,
} from '../services/survey.service';
import {
  GetSurveyBatchesParams,
  GetSurveySubmissionsParams,
  SendSurveyInput,
} from '../types/survey.types';

export const useGetSurveyBatches = (params: GetSurveyBatchesParams) => {
  return useGlobalQuery({
    queryKey: ['hr-survey-batches', params],
    queryFn: () => getSurveyBatches(params),
    staleTime: 60 * 1000,
  });
};

export const useGetSurveyBatchById = (itemId?: string) => {
  return useGlobalQuery({
    queryKey: ['hr-survey-batch', itemId],
    queryFn: () => getSurveyBatchById(itemId as string),
    enabled: Boolean(itemId),
  });
};

export const useGetSurveySubmissions = (params: GetSurveySubmissionsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-survey-submissions', params],
    queryFn: () => getSurveySubmissions(params),
    staleTime: 60 * 1000,
  });
};

export const useGetSurveySubmissionById = (itemId?: string) => {
  return useGlobalQuery({
    queryKey: ['hr-survey-submission', itemId],
    queryFn: () => getSurveySubmissionById(itemId as string),
    enabled: Boolean(itemId),
  });
};

export const useGetSurveyFormsLookup = () => {
  return useGlobalQuery({
    queryKey: ['hr-survey-forms-lookup'],
    queryFn: () => getSurveyFormsLookup(),
    staleTime: 30 * 60 * 1000,
  });
};

export const useGetSurveyFormLookupById = (formId?: string) => {
  return useGlobalQuery({
    queryKey: ['hr-survey-form-lookup', formId],
    queryFn: () => getSurveyFormLookupById(formId as string),
    enabled: Boolean(formId),
  });
};

export const useGetEmployeesLookup = (keyword: string) => {
  return useGlobalQuery({
    queryKey: ['hr-survey-employees-lookup', keyword],
    queryFn: () => getEmployeesLookup(keyword),
    staleTime: 60 * 1000,
  });
};

export const useSendSurvey = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: SendSurveyInput) => sendSurvey(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-survey-batches'] });
      toast({ title: t('SURVEY_SENT') });
    },
  });
};

export const useDeleteSurveyBatch = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteSurveyBatch(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-survey-batches'] });
      toast({ title: t('SURVEY_DELETED') });
    },
  });
};

export const useDeleteSurveySubmission = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteSurveySubmission(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-survey-submissions'] });
      toast({ title: t('SUBMISSION_DELETED') });
    },
  });
};
